import { useProject } from "../../../../hooks/query";
import { AddRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Typography,
  IconButton as JoyIconButton,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Chip,
} from "@mui/joy";
import {
  PackageCheck,
  ChevronDown,
  BookX,
  ListTodo,
  Building2,
  Heart,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { useParams } from "react-router-dom";

import { Skeleton, Stack } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";

import { Alert, IconButton } from "../../../../shared";
import { useResponsive } from "../../../../hooks";

export function SummaryCard() {
  const params = useParams();
  const id = params.id as string;

  const { isMobile } = useResponsive();

  const { useGetProjectById } = useProject();
  const getProjectByIdSvc = useGetProjectById(id);

  const projectDetails = getProjectByIdSvc.data;
  const isLoading = getProjectByIdSvc.isPending;

  if (getProjectByIdSvc.isError) {
    return (
      <Alert
        errorMessage={
          (getProjectByIdSvc.error as any)?.message ||
          "Failed to load project summary"
        }
      />
    );
  }

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <motion.div
          key="skeleton"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box display={"flex"} gap={1.5} alignItems={"center"}>
              <Skeleton
                variant="rectangular"
                width={44}
                height={44}
                sx={{ borderRadius: "12px" }}
              />
              <Stack spacing={0.5}>
                <Skeleton variant="text" width={60} height={12} />
                <Skeleton variant="text" width={220} height={28} />
              </Stack>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Skeleton
                variant="rectangular"
                width="40%"
                height={36}
                sx={{ borderRadius: "8px" }}
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={36}
                sx={{ borderRadius: "8px" }}
              />
            </Box>
          </Box>
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box display={"flex"} gap={1} alignItems={"center"}>
                <Card
                  sx={{ p: 1, borderRadius: "4px", minHeight: 0 }}
                  variant="solid"
                  color="primary"
                >
                  <Building2 size={18} strokeWidth={2} />
                </Card>
                <Box>
                  <Box display={"flex"} alignItems="baseline" gap={1}>
                    <Typography
                      level={"body-xs"}
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: 600,
                        opacity: 0.7,
                      }}
                    >
                      Project
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Typography
                      level={"title-md"}
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {projectDetails?.Name || "-"}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        size="md"
                        variant="soft"
                        color="primary"
                        startDecorator={<Sparkles size={16} />}
                        sx={{ borderRadius: "16px" }}
                      >
                        AI Review
                      </Chip>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  width: { xs: "100%", md: "auto" },
                  justifyContent: { xs: "space-between", md: "flex-end" },
                }}
              >
                <IconButton variant={"outlined"} size={"md"} color={"danger"}>
                  <Heart size={16} />
                </IconButton>
                {isMobile ? (
                  <JoyIconButton
                    variant="outlined"
                    color="neutral"
                    size="md"
                    sx={{
                      borderRadius: "4px",
                    }}
                  >
                    <AddRounded fontSize="small" />
                  </JoyIconButton>
                ) : (
                  <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<AddRounded fontSize="small" />}
                    size="md"
                    sx={{
                      borderRadius: "4px",
                      flex: { xs: 1, md: "initial" },
                    }}
                  >
                    Follow
                  </Button>
                )}
                <Dropdown>
                  <ButtonGroup
                    variant="outlined"
                    color="danger"
                    size="md"
                    sx={{
                      borderRadius: "4px",
                      flex: { xs: 2, md: "initial" },
                    }}
                  >
                    <Button
                      startDecorator={<PackageCheck size={16} />}
                      sx={{ flex: 1 }}
                    >
                      Mark as complete
                    </Button>
                    <MenuButton
                      slots={{ root: JoyIconButton }}
                      sx={{ px: 0.5 }}
                    >
                      <ChevronDown size={16} />
                    </MenuButton>
                  </ButtonGroup>
                  <Menu
                    placement="bottom-end"
                    size="md"
                    sx={{ width: 200, borderRadius: "4px", zIndex: 10001 }}
                  >
                    <MenuItem>
                      <BookX size={16} />
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.primary" }}
                      >
                        Mark as cancelled
                      </Typography>
                    </MenuItem>
                    <MenuItem>
                      <ListTodo size={16} />
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.primary" }}
                      >
                        Mark as incomplete
                      </Typography>
                    </MenuItem>
                  </Menu>
                </Dropdown>
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
